import { useState, useEffect, useRef, useContext } from "react";
import { brandMapping } from "~src/api/brandMapping";
import { BrandMappingContext } from "~src/contexts/BrandMappingContext";
import { mappingCountries } from "~src/data/mappingCounties";
import {
  CountryMapData,
  GeographicalSummaryData,
} from "~src/types/brandMapping";

const useBrandMapping = (
  searchText: string | undefined,
  exactMatch: boolean,
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [geographicalSummary, setGeographicalSummary] =
    useState<GeographicalSummaryData>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>();
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestInProgress = useRef(false);
  const { setMapOfBrandsContext } = useContext(BrandMappingContext) || {};

  const loadMore = async () => {
    if (
      searchText === undefined ||
      totalPages === currentPage ||
      requestInProgress.current
    )
      return;
    requestInProgress.current = true;
    try {
      setError(null);
      const nextPage = currentPage + 1;
      const modifiedSearchString = exactMatch ? `"${searchText}"` : searchText;
      const response = await brandMapping(modifiedSearchString, nextPage);
      const current_page = response.current_page;
      const mapping = response.data;
      setCurrentPage(current_page);

      const sortedMapping = mapping.sort((a, b) => {
        const totalA = a.total_product_count + a.total_ad_count;
        const totalB = b.total_product_count + b.total_ad_count;
        return totalB - totalA;
      });
      if (setMapOfBrandsContext) {
        setMapOfBrandsContext((prevItems) => {
          if (!prevItems) {
            return [...sortedMapping];
          }
          return [...prevItems, ...sortedMapping];
        });
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      requestInProgress.current = false;
    }
  };

  useEffect(() => {
    if (searchText === undefined) return;

    const filterCountries = (
      data: GeographicalSummaryData,
    ): GeographicalSummaryData => {
      const filterByPredefinedCountries = (countries: CountryMapData[]) =>
        countries.filter(([countryCode]) =>
          Object.prototype.hasOwnProperty.call(mappingCountries, countryCode)
        );

      return {
        top_ad_countries: filterByPredefinedCountries(data.top_ad_countries),
        top_brand_countries: filterByPredefinedCountries(
          data.top_brand_countries,
        ),
        top_shipping_countries: filterByPredefinedCountries(
          data.top_shipping_countries,
        ),
        top_traffic_countries: filterByPredefinedCountries(
          data.top_traffic_countries,
        ),
      };
    };

    const fetchMapping = async () => {
      try {
        setLoading(true);
        setInitialLoading(true);
        setError(null);
        const modifiedSearchString = exactMatch
          ? `"${searchText}"`
          : searchText;
        const response = await brandMapping(modifiedSearchString, 1);

        const current_page = response.current_page;
        const total_page = response.total_pages;
        const mapping = response.data;
        const geographicalSummary = filterCountries(
          response.geographical_summary,
        );
        setCurrentPage(current_page);
        setTotalPages(total_page);

        const sortedMapping = mapping.sort((a, b) => {
          const totalA = a.total_product_count + a.total_ad_count;
          const totalB = b.total_product_count + b.total_ad_count;
          return totalB - totalA;
        });

        setGeographicalSummary(geographicalSummary);
        if (setMapOfBrandsContext) {
          setMapOfBrandsContext(sortedMapping);
        }
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchMapping();
  }, [searchText, exactMatch, setMapOfBrandsContext]);

  return {
    loadMore,
    geographicalSummary,
    loading,
    initialLoading,
    error,
  };
};

export default useBrandMapping;
